
import React from "react";
import "./CaseStudiesSection.css";

import {
  ArrowRight,
  ExternalLink,
  Smartphone,
  BriefcaseBusiness,
  FlaskConical,
} from "lucide-react";

// =========================================================
// PROJECT IMAGES
// Location: src/images/HeroSectionImages
// =========================================================

// Dreamland Bites
import dreamlandWeb from "../../images/HeroSectionImages/dreamland-web.webp";
import dreamlandAdmin from "../../images/HeroSectionImages/dreamland-admin-dashboard.webp";

// Coconut Multiproduct
import coconutWeb from "../../images/HeroSectionImages/coconut-web.webp";
import daabJall from "../../images/HeroSectionImages/daab-jall-google-play.webp";

// Jabala MIS
import jabalaDashboard from "../../images/HeroSectionImages/Jabala_SupreAdmin_DashBoard.webp";
import jabalaLogin from "../../images/HeroSectionImages/Jabala_DashBoard.webp";

// Published Android Product
import mathGenius from "../../images/HeroSectionImages/math-genius-google-play.webp";

// Internal Products
import crmDashboard from "../../images/HeroSectionImages/BairagiConsultsDashboard.webp";
import pdfPageManager from "../../images/HeroSectionImages/pdf-page-manager.webp";

// R&D
import pressMed from "../../images/HeroSectionImages/pressmed-prototype.webp";
import jewelleryTryOn from "../../images/HeroSectionImages/jewellery-tryon-concept.webp";

// =========================================================
// PROJECT DATA
// =========================================================

const clientProjects = [
  {
    id: "dreamland",
    title: "Dreamland Bites",
    category: "Premium Bakery Ecommerce Platform",
    type: "CLIENT PROJECT",

    description:
      "Custom bakery ecommerce platform featuring a responsive storefront, product management, ordering workflows and a business administration dashboard.",

    tech: [
      "React",
      "FastAPI",
      "PostgreSQL",
      "Ecommerce",
    ],

    image: dreamlandWeb,
    additionalImage: dreamlandAdmin,

    link: "https://dreamlandbitess.com/",
    linkLabel: "View Live Website",
  },

  {
    id: "coconut",
    title: "Coconut Multiproduct",
    category: "Ecommerce Website & Android Application",
    type: "CLIENT PROJECT",

    description:
      "Digital commerce solution for coconut-based products, combining a web storefront with Daab Jall, an Android shopping application published on Google Play.",

    tech: [
      "Ecommerce",
      "Android",
      "Mobile App",
      "Google Play",
    ],

    image: coconutWeb,
    additionalImage: daabJall,

    link: "https://coconutmultiproduct.com/",
    linkLabel: "View Live Website",

    secondaryLink:
      "https://play.google.com/store/apps/details?id=com.coconutapp",
    secondaryLabel: "View Android App",
  },

  {
    id: "jabala",
    title: "Jabala MIS",
    category: "NGO Rehabilitation & Management Information System",
    type: "CLIENT PROJECT",

    description:
      "Custom management information system for beneficiary registration, centre administration, activities, role-based access and operational reporting.",

    tech: [
      "React",
      "FastAPI",
      "PostgreSQL",
      "MIS",
    ],

    image: jabalaDashboard,
    additionalImage: jabalaLogin,

    // No public project URL is assumed.
    link: null,
    linkLabel: null,
  },
];

const internalProjects = [
  {
    id: "math-genius",
    title: "Math Genius",
    category: "Android Educational Application",
    type: "INTERNAL PRODUCT",

    description:
      "Published Android educational game focused on mental mathematics, demonstrating mobile application development and Google Play publishing experience.",

    tech: [
      "Android",
      "Education",
      "Google Play",
    ],

    image: mathGenius,

    link:
      "https://play.google.com/store/apps/details?id=com.BairagiConsultants.MathQuiz",
    linkLabel: "View on Google Play",
  },

  {
    id: "crm-dashboard",
    title: "Business CRM Dashboard",
    category: "Business Management & Analytics",
    type: "INTERNAL PRODUCT",

    description:
      "Business dashboard concept covering revenue analytics, customer information, order monitoring, reporting and operational management workflows.",

    tech: [
      "CRM",
      "Analytics",
      "Dashboard",
    ],

    image: crmDashboard,

    link: null,
  },

  {
    id: "pdf-manager",
    title: "PDF Page Manager",
    category: "Document Processing Utility",
    type: "INTERNAL TOOL",

    description:
      "Document utility designed to upload PDF files, select pages for removal and export a modified PDF.",

    tech: [
      "PDF",
      "Documents",
      "Productivity",
    ],

    image: pdfPageManager,

    link: null,
  },
];

const researchProjects = [
  {
    id: "pressmed",
    title: "PressMed",
    category: "Digital Healthcare & E-Prescription",
    type: "INTERNAL R&D",

    description:
      "Healthcare technology prototype exploring digital prescriptions, patient information workflows and AI-assisted healthcare functionality.",

    tech: [
      "Healthcare",
      "E-Prescription",
      "AI",
    ],

    image: pressMed,

    link: null,
  },

  {
    id: "jewellery-tryon",
    title: "Jewellery Virtual TryOn",
    category: "AR-Powered Jewellery Shopping Concept",
    type: "INTERNAL R&D",

    description:
      "Virtual jewellery try-on concept exploring product visualization, jewellery placement and interactive retail experiences.",

    tech: [
      "AR",
      "Virtual Try-On",
      "Retail",
    ],

    image: jewelleryTryOn,

    link: null,
  },
];

// =========================================================
// REUSABLE PROJECT CARD
// =========================================================

const ProjectCard = ({ project }) => {
  return (
    <article className="project-card">

      {/* IMAGE */}

      <div className="project-image-wrapper">
        <img
          src={project.image}
          alt={`${project.title} software project by Bairagi Consultants`}
          className="project-image"
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* CONTENT */}

      <div className="project-content">

        <span
          className="project-category"
          style={{ display: "block", marginBottom: "10px" }}
        >
          {project.type}
        </span>

        <span className="project-category">
          {project.category}
        </span>

        <h3>{project.title}</h3>

        <p>{project.description}</p>

        {/* TECHNOLOGY TAGS */}

        <div className="project-tech">
          {project.tech.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>

        {/* PROJECT LINKS */}

        {project.link && (
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="project-link"
          >
            {project.linkLabel || "Explore Project"}
            <ExternalLink size={16} />
          </a>
        )}

        {project.secondaryLink && (
          <a
            href={project.secondaryLink}
            target="_blank"
            rel="noopener noreferrer"
            className="project-link"
            style={{
              marginTop: "12px",
              display: "inline-flex",
            }}
          >
            {project.secondaryLabel}
            <ExternalLink size={16} />
          </a>
        )}

      </div>
    </article>
  );
};

// =========================================================
// REUSABLE PROJECT SECTION
// =========================================================

const ProjectGroup = ({
  title,
  description,
  icon: Icon,
  projects,
}) => {
  return (
    <div
      className="project-group"
      style={{ marginTop: "60px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <Icon size={24} color="#3b82f6" />

        <h3
          style={{
            margin: 0,
            fontSize: "26px",
            fontWeight: 700,
          }}
        >
          {title}
        </h3>
      </div>

      <p
        style={{
          marginBottom: "28px",
          opacity: 0.8,
          maxWidth: "760px",
          lineHeight: 1.7,
        }}
      >
        {description}
      </p>

      <div className="projects-grid">
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
          />
        ))}
      </div>
    </div>
  );
};

// =========================================================
// CASE STUDIES SECTION
// =========================================================

const CaseStudiesSection = () => {
  const featuredProject = clientProjects[0];

  return (
    <section
      className="case-studies-section"
      id="case-studies"
    >
      <div className="case-studies-container">

        {/* ===============================================
            HEADER
        =============================================== */}

        <div className="case-studies-header">

          <p className="case-studies-label">
            CLIENT PROJECTS & SOFTWARE PORTFOLIO
          </p>

          <h2 className="case-studies-title">
            Real Business Systems
            <br />
            Built for Practical Needs
          </h2>

          <p className="case-studies-description">
            Explore software developed by Bairagi Consultants,
            including delivered client projects, published
            Android applications and internal technology
            initiatives.
          </p>

        </div>

        {/* ===============================================
            FEATURED CLIENT PROJECT
        =============================================== */}

        <div className="featured-case-study">

          <div className="featured-case-content">

            <span className="featured-badge">
              Featured Client Project
            </span>

            <h3>{featuredProject.title}</h3>

            <p className="featured-category">
              {featuredProject.category}
            </p>

            <p className="featured-description">
              Full-scale bakery ecommerce platform with
              a responsive storefront, product management,
              customer ordering workflows, promotional
              features and an administration dashboard.
            </p>

            <div className="featured-tech">
              {featuredProject.tech.map((item) => (
                <span key={item}>{item}</span>
              ))}

              <span>Cloud Deployment</span>
            </div>

            <a
              href={featuredProject.link}
              target="_blank"
              rel="noopener noreferrer"
              className="featured-link"
            >
              View Live Project
              <ArrowRight size={18} />
            </a>

          </div>

          <div className="featured-image-wrapper">
            <img
              src={featuredProject.image}
              alt="Dreamland Bites ecommerce website"
              className="featured-image"
              loading="eager"
            />
          </div>

        </div>

        {/* ===============================================
            CLIENT PROJECTS
        =============================================== */}

        <ProjectGroup
          title="Client Projects"
          description="Software solutions developed for real organizations, covering ecommerce, mobile applications and operational management."
          icon={BriefcaseBusiness}
          projects={clientProjects}
        />

        {/* ===============================================
            INTERNAL PRODUCTS
        =============================================== */}

        <ProjectGroup
          title="Internal Products & Tools"
          description="Independently developed applications and utilities demonstrating BC's engineering and product development capabilities."
          icon={Smartphone}
          projects={internalProjects}
        />

        {/* ===============================================
            RESEARCH & DEVELOPMENT
        =============================================== */}

        <ProjectGroup
          title="Innovation & R&D"
          description="Technology prototypes and exploratory solutions. These projects demonstrate technical direction rather than verified customer deployments."
          icon={FlaskConical}
          projects={researchProjects}
        />

      </div>
    </section>
  );
};

export default CaseStudiesSection;
